import customFetch from '../../utils/customFetch'
import { realEstateToApp } from './converters/toApp/realEstate'
import { API_ENDPOINT } from '../../config'
import { ConvertedRealEstate, RealEstate, RealEstateDTO } from '../../@types/real-estate-api-types'
import { parseJSON, safeParseJSON } from '../../utils/apiHelper/apiHelper'
import { ContentStateInterface } from '../../@types/types'

const offerApiUrl = API_ENDPOINT + '/anbieten/private-anbieter/inserieren/api/realEstate/offer-api'

const options = {
  method: 'GET',
  headers: {
    Accept: 'application/json; charset=utf-8',
    'Content-Type': 'application/json; charset=UTF-8',
  },
  credentials: 'include',
}

export const saveAndGetRealEstateListing = async (
  uuid: string,
  realEstateObject: RealEstateDTO
): Promise<RealEstate> => {
  const parameters = new URLSearchParams()
  parameters.append('updateObject', 'true')

  const options = {
    method: 'PUT',
    headers: {
      Accept: 'application/json; charset=utf-8',
      'Content-Type': 'application/json; charset=UTF-8',
    },
    credentials: 'include',
    body: JSON.stringify(realEstateObject),
  }

  const response = await customFetch(offerApiUrl + `/${uuid}\?${parameters.toString()}`, options)
  if (!response.ok) {
    const text = await response.text()
    const errorPayload = safeParseJSON(text)
    const errorDetail = errorPayload?.detail ?? errorPayload?.details

    if (response.status === 403) {
      throw new AccessForbiddenError(
        `Failed to save listing id:${realEstateObject.estate.id},category:${realEstateObject.estate.category},transaction:${realEstateObject.estate.transaction} with status ${response.status} and response ${text}`
      )
    } else if (response.status === 404 && errorDetail === 'REAL_ESTATE_NOT_FOUND') {
      throw new RealEstateNotFoundError(
        `Failed to save listing id:${realEstateObject.estate.id},category:${realEstateObject.estate.category},transaction:${realEstateObject.estate.transaction} with status ${response.status} and response ${text}`
      )
    } else if (response.status === 404 && errorDetail === 'CLB_REAL_ESTATE_NOT_FOUND') {
      throw new ClbRealEstateNotFoundError(
        `Failed to save listing id:${realEstateObject.estate.id},category:${realEstateObject.estate.category},transaction:${realEstateObject.estate.transaction} with status ${response.status} and response ${text}`
      )
    } else {
      throw new Error(
        `Failed to save listing id:${realEstateObject.estate.id},category:${realEstateObject.estate.category},transaction:${realEstateObject.estate.transaction} with status ${response.status} and response ${text}`
      )
    }
  }
  return response.json()
}

export const getEstateByCLBUuid: (
  uuid: string,
  defaultStateValues: ContentStateInterface
) => Promise<ConvertedRealEstate | null> = async (uuid, defaultStateValues) => {
  const response = await customFetch(
    `${API_ENDPOINT}/anbieten/private-anbieter/inserieren/api/realEstate/${uuid}`,
    options
  )

  if (response.status === 404) {
    const text = await response.text()
    const errorPayload = safeParseJSON(text)
    const errorDetail = errorPayload?.detail ?? errorPayload?.details

    if (errorDetail === 'REAL_ESTATE_NOT_FOUND') {
      throw new RealEstateNotFoundError(`Listing ${uuid} has been deleted`)
    }

    return null
  }

  const result: RealEstate | null = await parseJSON(response)
  return result ? realEstateToApp(result, defaultStateValues) : null
}

export const checkIfOtherExternalIdExist: (externalId: string, hofRealEstateId: string) => Promise<boolean> = async (
  externalId,
  hofRealEstateId
) => {
  return await customFetch(
    `${API_ENDPOINT}/anbieten/private-anbieter/inserieren/api/realEstate/externalId/${externalId}/exist?hofRealEstateId=${hofRealEstateId}`,
    options
  ).then((r) => r.json())
}

export class AccessForbiddenError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'AccessForbiddenError'
    Object.setPrototypeOf(this, AccessForbiddenError.prototype)
  }
}

export class RealEstateNotFoundError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'RealEstateNotFoundError'
    Object.setPrototypeOf(this, RealEstateNotFoundError.prototype)
  }
}

export class ClbRealEstateNotFoundError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ClbRealEstateNotFoundError'
    Object.setPrototypeOf(this, ClbRealEstateNotFoundError.prototype)
  }
}
